import React, { useState, useMemo, useCallback } from "react";
import styles from "../../../styles/pages/profile/ProfilePage.module.css";
import { FaHeart } from "react-icons/fa";
import FavoritesList from "../../product/FavoritesList";
import ConfirmModal from "../../common/ConfirmModal";
import useBulkSelection from "../bulk/useBulkSelection";
import BulkSelectBar, { SelectModeButton } from "../bulk/BulkSelectBar";

const FavoritesView = ({
  favorites,
  loading = false,
  isError = false,
  onRetry,
  onDelete,
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [refreshFavorites, setRefreshFavorites] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const favoriteIds = useMemo(() => {
    if (!favorites || favorites.length === 0) return [];
    return typeof favorites[0] === "object"
      ? favorites.map((p) => p._id || p.id)
      : favorites;
  }, [favorites]);

  const {
    selectMode,
    selectedIds: selectedFavorites,
    setSelectedIds: setSelectedFavorites,
    allVisibleSelected,
    toggleSelectMode,
    selectAllVisible,
    exitSelectMode,
  } = useBulkSelection(favoriteIds);

  const handleDeleteConfirm = useCallback(async () => {
    if (selectedFavorites.length === 0) {
      setIsConfirmModalOpen(false);
      return;
    }

    try {
      setDeleting(true);
      await onDelete(selectedFavorites);
      exitSelectMode();
      setRefreshFavorites((prev) => !prev);
      setIsConfirmModalOpen(false);
    } catch {
      setIsConfirmModalOpen(false);
    } finally {
      setDeleting(false);
    }
  }, [selectedFavorites, onDelete, exitSelectMode]);

  const handleOpenConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  return (
    <>
      <div className={styles.contentSection}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>
              <FaHeart className={styles.sectionIcon} />
              My Favorites
            </h2>
            {favoriteIds.length > 0 && (
              <p className={styles.sectionSubtitle}>
                {favoriteIds.length} saved
                {favoriteIds.length === 1 ? " item" : " items"}
              </p>
            )}
          </div>
          {favoriteIds.length > 0 && (
            <div className={styles.bulkActions}>
              <SelectModeButton active={selectMode} onClick={toggleSelectMode} />
            </div>
          )}
        </div>

        {selectMode && (
          <BulkSelectBar
            selectedCount={selectedFavorites.length}
            allSelected={allVisibleSelected}
            busy={deleting}
            onSelectAll={selectAllVisible}
            onDelete={handleOpenConfirmModal}
            noun="favorites"
          />
        )}

        {isError ? (
          <div className={styles.emptyState}>
            <p>We couldn't load your favorites. Please try again.</p>
            {onRetry && (
              <button className="buttonSecondary" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        ) : (
          <FavoritesList
            favoriteIds={favoriteIds}
            loadingIds={loading}
            isEdit={selectMode}
            selectedFavorites={selectedFavorites}
            setSelectedFavorites={setSelectedFavorites}
            refresh={refreshFavorites}
          />
        )}
      </div>

      {isConfirmModalOpen && (
        <ConfirmModal
          message={
            selectedFavorites.length === 1
              ? "Remove this favorite?"
              : `Remove ${selectedFavorites.length} favorites?`
          }
          onConfirm={handleDeleteConfirm}
          onClose={handleCloseConfirmModal}
        />
      )}
    </>
  );
};

export default React.memo(FavoritesView);
